import { useState, useCallback } from 'react';
import { DETAILED_STEPS } from '../../components/estimation/detailed/config';
import { validateDetailedEstimateData } from '../../utils/detailedestimation/validator';
import { calculateDetailedEstimate } from '../../utils/detailedestimation/calculator';

export const useDetailedEstimate = (initialData?: any) => {
  const [currentStep, setCurrentStep] = useState(
    // Si on a des données initiales, commencer à la deuxième étape
    initialData ? DETAILED_STEPS[1].id : DETAILED_STEPS[0].id
  );
  const [formData, setFormData] = useState(initialData || {});
  const [completedSteps, setCompletedSteps] = useState<string[]>(
    // Si on a des données initiales, marquer la première étape comme complétée
    initialData ? ['description'] : []
  );
  const [error, setError] = useState<string | null>(null);

  const handleStepDataChange = useCallback((stepId: string, data: any) => {
    // Empêcher la modification de la description si on édite une estimation existante
    if (initialData && stepId === 'description') {
      return;
    }

    setFormData(prev => ({
      ...prev,
      [stepId]: data
    }));
    setError(null);
  }, [initialData]);

  const handleStepClick = useCallback((stepId: string) => {
    // Empêcher le retour à l'étape description si on édite une estimation existante
    // ou si elle a déjà été complétée
    if ((initialData || completedSteps.includes('description')) && stepId === 'description') {
      return;
    }

    const targetIndex = DETAILED_STEPS.findIndex(s => s.id === stepId);
    const currentIndex = DETAILED_STEPS.findIndex(s => s.id === currentStep);

    // Permettre la navigation vers les étapes accessibles
    if (targetIndex <= currentIndex + 1 || completedSteps.includes(stepId)) {
      setCurrentStep(stepId);
    }
  }, [currentStep, completedSteps, initialData]);

  const handleNext = useCallback(() => {
    const currentIndex = DETAILED_STEPS.findIndex(step => step.id === currentStep);
    
    // Si c'est la dernière étape, valider l'estimation
    if (currentIndex === DETAILED_STEPS.length - 1) {
      try {
        calculateDetailedEstimate(formData);
      } catch (err) {
        setError(err.message);
        return false;
      }
    }

    if (currentIndex < DETAILED_STEPS.length - 1) {
      // Marquer l'étape comme complétée
      setCompletedSteps(prev => [...new Set([...prev, currentStep])]);
      setCurrentStep(DETAILED_STEPS[currentIndex + 1].id);
      return true;
    }
    return false;
  }, [currentStep, formData]);

  const handleBack = useCallback(() => {
    const currentIndex = DETAILED_STEPS.findIndex(step => step.id === currentStep);
    
    // Empêcher le retour à l'étape description si elle est complétée
    if (currentIndex > 1 || (currentIndex === 1 && !completedSteps.includes('description'))) {
      setCurrentStep(DETAILED_STEPS[currentIndex - 1].id);
      return true;
    }
    return false;
  }, [currentStep, completedSteps]);

  const calculateEstimate = useCallback(() => {
    try {
      return calculateDetailedEstimate(formData);
    } catch (err) {
      console.error('Erreur lors du calcul:', err);
      return null;
    }
  }, [formData]);

  return {
    currentStep,
    formData,
    completedSteps,
    error,
    handleStepClick,
    handleNext,
    handleBack,
    handleStepDataChange,
    calculateEstimate
  };
};